import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Route,
  Redirect,
  withRouter,
} from 'react-router-dom';
import { Render } from '@puckeditor/core';
import { injectIntl } from 'react-intl';
import classnames from 'classnames';
import { validate as uuidValidate } from 'uuid';
import {
  getSectionFrontendComponent,
  getPageUrl,
  sectionComponents,
  getTransparentHeaderPaddingClass,
  setPageFileModalScrollLock,
} from '../helpers/PageSections';

import { APIgetPage } from '../api/Pages';
import { fetchPageMetadata } from '../actions/Metadata';

import Loading from '../components/Loading';
import NothingFound from '../components/NothingFound';
import File from './File';
import PageCollectionView from './PageCollectionView';
import ExternalSearchResults from './SearchResults/ExternalSearchResults';
import PageHeader from '../components/Page/Header';

import { cleanLoginParams } from '../helpers/App';
import { getPuckConfig } from '../helpers/PuckConfig';
import { PageIdContext } from '../helpers/PageIdContext';

class PageInternal extends Component {
  constructor(props) {
    super(props);

    this.pageContextValue = {
      pageId: null,
      pageRouteId: null,
      isEditing: false,
      pageBasePath: 'content',
      visibilityMode: null,
    };

    this.state = {
      page: null,
      redirect: false,
      notFound: false,
      error: null,
    };

    this.previousLocation = props.location;
  }

  componentDidMount() {
    const { location } = this.props;
    this.fetchPage();
    setPageFileModalScrollLock(this.isOverlayModalRoute(location));
  }

  componentDidUpdate(prevProps) {
    const { match, location } = this.props;
    const { pageId } = match.params;
    const { pageId: prevPageId } = prevProps.match.params;

    if (!this.isFileModalRoute(location)) {
      this.previousLocation = location;
    }

    const isModal = this.isOverlayModalRoute(location);
    const wasModal = this.isOverlayModalRoute(prevProps.location);

    if (isModal !== wasModal) {
      setPageFileModalScrollLock(isModal);
    }

    if (pageId !== prevPageId) {
      this.fetchPage();
    }
  }

  // eslint-disable-next-line class-methods-use-this
  componentWillUnmount() {
    setPageFileModalScrollLock(false);
  }

  // eslint-disable-next-line class-methods-use-this
  getUuidFromPath(location, routeType) {
    const pathname = location?.pathname || '';
    const matchedPath = pathname.match(new RegExp(`^/content/[^/]+/${routeType}/([^/]+)$`, 'i'));
    return matchedPath?.[1] || '';
  }

  isFileModalRoute(location) {
    const fileUuid = this.getUuidFromPath(location, 'file');

    return !!((
      (
        location.state
        && location.state.modal
        && this.previousLocation !== location
      )
      || uuidValidate(fileUuid)
    ));
  }

  isCollectionModalRoute(location) {
    const collectionUuid = this.getUuidFromPath(location, 'collection');
    return uuidValidate(collectionUuid);
  }

  isOverlayModalRoute(location) {
    return this.isFileModalRoute(location) || this.isCollectionModalRoute(location) ||
      /^\/content\/[^/]+\/search(?:\/collections\/[^/]+)?\/?$/.test(location.pathname);
  }

  getPageId() {
    const {
      match,
      pageId: pageIdFromProps,
    } = this.props;
    const { pageId: pageIdFromUrl } = match.params;
    return pageIdFromUrl || pageIdFromProps;
  }

  fetchPage() {
    const { dispatch } = this.props;
    const pageId = this.getPageId();
    this.pageMetadataController?.abort();
    this.pageMetadataController = new AbortController();
    const { signal } = this.pageMetadataController;
    APIgetPage(pageId).then((response) => {
      if (signal.aborted) return;
      this.setState({ page: response.data });
      dispatch(fetchPageMetadata(response.data.id, { signal }));
    }).catch((error) => {
      if (signal.aborted) return;
      if (error.response?.status === 403 && error.response?.data?.error === 'login_required') {
        this.setState({ redirect: true });
        return;
      }

      if (error.response?.status === 404) {
        this.setState({ notFound: true });
      }
    });
  }

  render() {
    const { location, intl } = this.props;
    const {
      page,
      error,
      redirect,
      notFound,
    } = this.state;

    const isFileModal = this.isFileModalRoute(location);
    const isCollectionModal = this.isCollectionModalRoute(location);
    const searchLocation = isFileModal ? this.previousLocation : location;
    const isSearchPage = /^\/content\/[^/]+\/search(?:\/collections\/[^/]+)?\/?$/.test(searchLocation.pathname);
    const shouldRenderCollectionUnderFileModal =
      isFileModal && this.isCollectionModalRoute(this.previousLocation);
    const shouldRenderCollectionModal =
      isCollectionModal || shouldRenderCollectionUnderFileModal;
    const collectionModalLocation = shouldRenderCollectionUnderFileModal
      ? this.previousLocation
      : location;

    if (redirect) {
      return (
        <Redirect
          to={{
            pathname: '/login',
            search: location.search,
            state: {
              from: `${location.pathname}${cleanLoginParams(location.search)}`,
            },
          }}
        />
      );
    }

    if (notFound) {
      return (
        <div className="main-content split-content">
          <NothingFound />
        </div>
      );
    }

    if (error) {
      return (
        <div className="main-content split-content">
          <p className="center">
            {error}
          </p>
        </div>
      );
    }

    if (!page) {
      return (
        <div className="main-content split-content">
          <div className="landing-content">
            <Loading />
          </div>
        </div>
      );
    }

    if (!page.options.page_type || page.options.page_type === 'fullscreen') {
      return (
        <Redirect
          to={{
            pathname: getPageUrl(page),
            search: location.search,
            state: {
              from: location.pathname,
            },
          }}
        />
      );
    }

    const pageRouteId = this.getPageId();
    const pageId = page?.id || pageRouteId;
    const pageAccentMode = page?.options?.accent_color_mode === 'custom' ? 'custom' : 'workspace';
    const pageAccentCustom = page?.options?.accent_color_custom || '';
    const useCustomPageAccent = pageAccentMode === 'custom' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(pageAccentCustom);
    const config = getPuckConfig(intl);
    const puckSections = (page.sections || [])
      .filter(section => section.type && config.components[section.type]);
    const disableTransparentHeaderPadding = true;
    const transparentHeaderPaddingClass = getTransparentHeaderPaddingClass(
      page?.options?.header,
      disableTransparentHeaderPadding,
    );

    if (
      this.pageContextValue.pageId !== pageId
      || this.pageContextValue.pageRouteId !== pageRouteId
      || this.pageContextValue.visibilityMode !== (
        page?.visibility_mode || page?.options?.visibility_mode || null
      )
    ) {
      this.pageContextValue = {
        ...this.pageContextValue,
        pageId,
        pageRouteId,
        visibilityMode:
          page?.visibility_mode || page?.options?.visibility_mode || null,
      };
    }

    return (
      <PageIdContext.Provider value={this.pageContextValue}>
        <div
          className={classnames('main-content', 'layout', 'layout-landing', {
            'page-accent-custom': useCustomPageAccent,
          })}
          style={useCustomPageAccent ? { '--page-accent-color': pageAccentCustom } : {}}
        >
          <div className={`landing-content page-header-${page?.options?.header}${useCustomPageAccent ? ' page-accent-custom' : ''}${transparentHeaderPaddingClass}`}>
            {puckSections.length > 0 ? (
              <Render
                config={config}
                data={{
                  content: puckSections
                    .map(section => ({
                      type: section.type,
                      props: { id: section.id, ...section },
                    })),
                  zones: page.options?.puck_zones || {},
                  root: {
                    props: {
                      name: page.name || '',
                      url: page.url || '',
                      header: page.options?.header || 'solid',
                      disableTransparentHeaderPadding,
                      accentColor: {
                        mode: pageAccentMode,
                        color: pageAccentCustom || '#203444',
                      },
                      // eslint-disable-next-line no-underscore-dangle
                      _bg: page.options?._bg || {
                        type: 'none', color: '#ffffff', gradientStart: '#ffffff', gradientEnd: '#000000', gradientDeg: 135,
                      },
                    },
                  },
                }}
              />
            ) : (
              page.sections.map((section) => {
                const component = getSectionFrontendComponent(section.section);
                const SectionContent = sectionComponents[component];
                if (!SectionContent) {
                  return null;
                }
                return (
                  <SectionContent
                    key={section.id}
                    section={section}
                    page={page}
                  />
                );
              })
            )}
          </div>
        </div>
        {isSearchPage && (
          <Route
            path="/content/:pageId/search"
            location={searchLocation}
            render={() => (
              <ExternalSearchResults
                entityType="custom-page"
                entityId={pageId}
                pageHeader={(
                  <PageHeader
                    headerType={page.options.header}
                    pageLogoUrl={page.options.logo_url_preview}
                    showSearchBar={
                      page.options?.showSearchBar ?? page.options?.show_search_bar ?? true
                    }
                    pageNavigation={
                      page.options?.pageNavigation || page.options?.page_navigation || []
                    }
                    pageBasePath="content"
                    pageRouteId={pageRouteId}
                    pageId={pageId}
                  />
                )}
              />
            )}
          />
        )}
        {isFileModal ? (
          <Route
            path="/content/:pageId/file/:fileId"
            render={() => (
              <File originalLocation={this.previousLocation} fixedModal={true} />
            )}
          />
        ) : null}
        {shouldRenderCollectionModal ? (
          <Route
            path="/content/:pageId/collection/:collectionUuid"
            location={collectionModalLocation}
            render={() => (
              <PageCollectionView
                originalLocation={this.previousLocation}
                fixedModal={true}
              />
            )}
          />
        ) : null}
      </PageIdContext.Provider>
    );
  }
}

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

PageInternal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  pageId: PropTypes.string,
};

PageInternal.defaultProps = {
  pageId: null,
};

export default injectIntl(withRouter(connect(mapStateToProps)(PageInternal)));
